import { QueryKeys } from '@common/queries/common';
import { TeamSettingsQuery } from '@prophecy/queries/pages/UserSettings/queries/team';
import { RunAttributionType } from 'frontend/core/src/data/apis/types';
import { useCallback, useMemo } from 'react';

import { useUser } from '../../../../context/user';
import { useGraphQlQuery } from '../../../../data/util';

type Member = { _id: string; firstName?: string | null; lastName?: string | null; email?: string | null };

export const FORMER_MEMBER_LABEL = 'Former member';

export type ResolvedPerson = { name: string; email?: string; isFormerMember?: boolean };

export type ResolvePerson = (attribution: RunAttributionType<string>, teamId?: string) => ResolvedPerson | undefined;

/**
 * One lookup for the whole page: every team the viewer belongs to, which covers the team of every fabric
 * Run History can list.
 */
export function useRunPersonResolver(): ResolvePerson {
  const uid = useUser()?.user?.id;
  const variables = { uid };
  const { data } = useGraphQlQuery(
    [QueryKeys.Team, 'settings', variables],
    TeamSettingsQuery,
    { enabled: Boolean(uid) },
    {
      uid: uid as string
    }
  );

  const isTeamLoaded = Boolean(data);
  const { membersByTeam, allMembers } = useMemo(() => {
    const membersByTeam = new Map<string, Map<string, Member>>();
    const allMembers = new Map<string, Member>();
    data?.User?.teams?.forEach((team) => {
      const teamMembers = new Map<string, Member>();
      [...(team.members || []), ...(team.admins || [])].forEach((member) => {
        teamMembers.set(String(member._id), member);
        allMembers.set(String(member._id), member);
      });
      membersByTeam.set(String(team._id), teamMembers);
    });
    return { membersByTeam, allMembers };
  }, [data]);

  // PRD resolution order: the fabric's team member, then the email recorded on the run, then "Former member"
  return useCallback(
    ({ userId, email }, teamId) => {
      if (!userId) return undefined;
      const members = (teamId && membersByTeam.get(teamId)) || allMembers;
      const member = members.get(userId);
      if (member) {
        const fullName = [member.firstName, member.lastName].filter(Boolean).join(' ');
        const memberEmail = member.email || email || undefined;
        return { name: fullName || memberEmail || 'Team member', email: memberEmail };
      }
      if (email) return { name: email, email };
      // while the team is loading or after it fails, an unmatched id says nothing about whether they left
      if (!isTeamLoaded) return undefined;
      return { name: FORMER_MEMBER_LABEL, isFormerMember: true };
    },
    [membersByTeam, allMembers, isTeamLoaded]
  );
}
